<div
  #reviewRoot
  class="step-review"
  [class.step-review--compact]="layoutDensity() === 'compact'"
  [class.step-review--compact-tight]="layoutDensity() === 'compact-tight'"
>
  <header class="review-top">
    <div class="delivery-segments" role="radiogroup" aria-label="How should selected posts go out?">
      <button
        type="button"
        class="delivery-segment"
        [class.delivery-segment--active]="deliveryMode() === 'schedule_each'"
        (click)="setDeliveryMode('schedule_each')"
      >
        Schedule each
      </button>
      <button
        type="button"
        class="delivery-segment"
        [class.delivery-segment--active]="deliveryMode() === 'posting_schedule'"
        [class.delivery-segment--disabled]="!hasConnectedAccounts"
        [disabled]="!hasConnectedAccounts"
        (click)="setDeliveryMode('posting_schedule')"
      >
        Posting schedule
      </button>
      <button
        type="button"
        class="delivery-segment"
        [class.delivery-segment--active]="deliveryMode() === 'draft'"
        (click)="setDeliveryMode('draft')"
      >
        Save as drafts
      </button>
    </div>

    @if (readinessSummary) {
      <p class="readiness-summary" aria-live="polite">{{ readinessSummary }}</p>
    }

    <ol class="readiness-pills" aria-label="Steps to finish">
      @for (step of readinessSteps; track step.id; let index = $index) {
        <li
          class="readiness-pill"
          [class.readiness-pill--done]="step.complete"
          [class.readiness-pill--current]="isCurrentReadinessStep(index)"
        >
          <span class="readiness-pill__marker" aria-hidden="true">
            @if (step.complete) {
              ✓
            } @else {
              {{ index + 1 }}
            }
          </span>
          <span class="readiness-pill__label">{{ step.label }}</span>
        </li>
      }
    </ol>

    @if (deliveryMode() === 'posting_schedule') {
      <a
        class="review-top__link"
        routerLink="/post-schedule"
        target="_blank"
        rel="noopener noreferrer"
        >Edit posting times ↗</a
      >
    }
  </header>

  @if (topAlertMessage) {
    <div class="review-alert" [class.review-alert--info]="hasPendingSelectedImages" role="alert">
      {{ topAlertMessage }}
    </div>
  }

  <div class="review-workspace">
    @if (requiresAccounts && !hasConnectedAccounts) {
      <div
        id="review-connect-accounts"
        class="no-accounts-notice"
        [class.no-accounts-notice--highlight]="highlightTarget() === 'connect-accounts'"
      >
        No connected accounts yet. <strong>Save as drafts</strong> to keep your posts, or connect an
        account to publish.
        <a
          class="no-accounts-notice__link"
          [routerLink]="['/workspace/settings']"
          [queryParams]="{ selectedTab: 'connected-accounts' }"
        >
          Connect accounts →
        </a>
      </div>
    }

    @if (requiresAccounts && hasConnectedAccounts) {
      <div
        id="review-select-accounts"
        class="accounts-row"
        [class.accounts-row--attention]="requiresAccounts && !hasSelectedAccounts"
        [class.accounts-row--highlight]="highlightTarget() === 'select-accounts'"
      >
        <span class="accounts-row__label">Accounts</span>
        <div class="accounts-row__list">
          @for (account of accounts(); track account.uuid) {
            <button
              type="button"
              class="account-chip"
              [class.account-chip--selected]="selectedAccountUuids().includes(account.uuid)"
              (click)="toggleAccount(account.uuid)"
              [attr.aria-label]="getAccountLabel(account)"
              [attr.title]="
                getAccountLabel(account) + ' (' + getProviderLabel(account.provider) + ')'
              "
            >
              <span class="account-chip__avatar">
                @if (getAccountAvatar(account)) {
                  <img [src]="getAccountAvatar(account)" alt="" class="account-chip__avatar-img" />
                } @else {
                  <span class="account-chip__avatar-fallback">{{
                    getAccountInitial(account)
                  }}</span>
                }
                <span class="account-chip__provider">
                  <img
                    [src]="'/assets/images/logo/' + getProviderIcon(account.provider) + '.svg'"
                    alt=""
                    onerror="
                      this.style.display = 'none';
                      this.parentElement?.classList.add('account-chip__provider--fallback');
                    "
                  />
                  <span class="account-chip__provider-fallback">{{
                    getProviderInitial(account.provider)
                  }}</span>
                </span>
              </span>
              <span class="account-chip__label">{{ getAccountLabel(account) }}</span>
            </button>
          }
        </div>
      </div>
    }

    <div class="posts-toolbar">
      <div class="review-actions">
        <button type="button" class="btn-link" (click)="selectAll()">Select all</button>
        <span class="action-divider">·</span>
        <button type="button" class="btn-link" (click)="deselectAll()">Deselect all</button>
        @if (showScheduleFields && selectedCount > 1) {
          <span class="action-divider">·</span>
          <button type="button" class="btn-link" (click)="spreadAcrossWeek()">
            Spread across weekdays
          </button>
        }
      </div>
      @if (isImageProcessing) {
        <div class="processing-notice">
          <span class="loader-spinner loader-spinner--small" aria-hidden="true"></span>
          <span>Generating images ({{ processingImageCount }})</span>
        </div>
      }
    </div>

    <div
      class="post-tiles"
      [class.post-tiles--single]="posts().length === 1"
      [class.post-tiles--double]="posts().length === 2"
      [class.post-tiles--triple]="posts().length === 3"
    >
      @for (post of posts(); track post.id) {
        <article
          class="post-tile"
          [class.post-tile--selected]="post.isSelected"
          [class.post-tile--schedule-missing]="isPostScheduleIncomplete(post)"
        >
          <div class="post-tile__head">
            <button
              type="button"
              class="post-tile__checkbox"
              [class.post-tile__checkbox--checked]="post.isSelected"
              (click)="togglePost(post.id, $event)"
              [attr.aria-label]="post.isSelected ? 'Deselect post' : 'Select post'"
            >
              <span class="checkbox-icon">✓</span>
            </button>
            <button
              type="button"
              class="btn-link post-tile__edit"
              (click)="openPreview(post, $event)"
            >
              Edit
            </button>
          </div>

          <button
            type="button"
            class="post-tile__thumb"
            (click)="openPreview(post, $event)"
            [attr.aria-label]="'Preview post for ' + post.day"
          >
            @if (post.hasImage) {
              @if (post.imageUrl) {
                <img [src]="post.imageUrl" alt="" class="post-tile__thumb-img" />
              } @else if (post.isImageLoading) {
                <span class="post-tile__thumb-loading">
                  <span class="loader-spinner loader-spinner--small" aria-hidden="true"></span>
                </span>
              } @else {
                <span class="post-tile__thumb-emoji">{{ post.emoji }}</span>
              }
            } @else {
              <span class="post-tile__thumb-text">{{
                getEditablePostText(post) | slice: 0 : 60
              }}</span>
            }
          </button>

          <p class="post-tile__caption">
            {{ getEditablePostText(post) | slice: 0 : 120
            }}{{ getEditablePostText(post).length > 120 ? '…' : '' }}
          </p>

          @if (showScheduleFields && post.isSelected) {
            <div class="post-tile__schedule" (click)="$event.stopPropagation()">
              <label class="schedule-field schedule-field--compact">
                <span>Date</span>
                <input
                  type="date"
                  [min]="minScheduleDate"
                  [value]="post.scheduledDate || ''"
                  (change)="onScheduleDateChange(post.id, $any($event.target).value)"
                />
              </label>
              <label class="schedule-field schedule-field--compact">
                <span>Time</span>
                <input
                  type="time"
                  [value]="post.scheduledTime || ''"
                  (change)="onScheduleTimeChange(post.id, $any($event.target).value)"
                />
              </label>
            </div>
          } @else if (!showScheduleFields) {
            <p class="post-tile__meta">{{ post.day }} · {{ post.time }}</p>
          }
        </article>
      }
    </div>
  </div>

  <footer class="step-footer">
    <div class="step-footer__left">
      @if (canConfirm) {
        <p class="step-footer__note">{{ deliveryModeHint }}</p>
      }
    </div>
    <div class="step-buttons">
      <button type="button" class="btn btn--ghost" (click)="service.goToStep(1)">
        ↺ Regenerate
      </button>
      <button
        type="button"
        class="btn btn--primary"
        [disabled]="isConfirmDisabled"
        (click)="confirmDelivery()"
      >
        {{ isPersisting() ? 'Saving...' : confirmLabel }}
      </button>
    </div>
  </footer>
</div>

@if (previewPost(); as post) {
  <div class="preview-overlay" (click)="closePreview()">
    <div class="preview-single" (click)="$event.stopPropagation()" role="dialog">
      <button type="button" class="preview-close" (click)="closePreview()" aria-label="Close">
        ×
      </button>
      <div class="preview-card">
        <div class="preview-card__header">
          <div class="preview-card__avatar">{{ post.emoji }}</div>
          <div class="preview-card__meta">
            <span class="preview-card__name">Your Brand</span>
            <span class="preview-card__time">{{ post.day }} · {{ post.time }}</span>
          </div>
          <div class="preview-card__platforms">
            @for (p of post.platforms; track p) {
              <span class="platform-tag">{{ p }}</span>
            }
          </div>
        </div>
        <div class="preview-card__image" [class.preview-card__image--no-image]="!post.hasImage">
          @if (post.hasImage) {
            @if (post.imageUrl) {
              <img [src]="post.imageUrl" alt="Generated image" class="preview-card__image-img" />
            } @else if (post.isImageLoading) {
              <div class="preview-card__image-loading">
                <span class="loader-spinner" aria-hidden="true"></span>
                <span>Generating image...</span>
              </div>
            } @else {
              <div class="preview-card__image-placeholder">
                <span class="preview-card__emoji">{{ post.emoji }}</span>
                <span>AI-generated image</span>
              </div>
            }
          } @else {
            <div class="preview-card__text-only">{{ getEditablePostText(post) }}</div>
          }
        </div>
        <div class="preview-card__editor" (click)="$event.stopPropagation()">
          <div class="preview-card__editor-banner" role="status">
            <span class="preview-card__editor-banner__icon" aria-hidden="true">✎</span>
            <span>Edit post text before you schedule or save.</span>
          </div>
          <div class="editor-field">
            <label class="editor-label" for="ai-preview-content">Post content</label>
            <textarea
              id="ai-preview-content"
              class="editor-textarea"
              rows="10"
              [ngModel]="getEditablePostText(post)"
              (ngModelChange)="onPreviewContentChange($event)"
              placeholder="Edit your post..."
            ></textarea>
          </div>
        </div>
        <div class="preview-card__actions">
          <span>Like</span>
          <span>Comment</span>
          <span>Share</span>
        </div>
      </div>
    </div>
  </div>
}
